/**
 * Attached-policies utility helpers.
 *
 * The UI sends a generic `{ service, actions: { view, create, edit, delete, ... } }`
 * shape. The backend maps action names → feature-specific statement names at
 * evaluation time, so the frontend stays free of statement-name knowledge.
 */

import type { AttachedPolicyEntry } from '../services/api/types'
import type { FeatureAction } from '../types/permissions'

/** Canonical `attached_policies[].service` for Connections (matches backend CONNECTORS_FEATURE_ACCESS). */
const CONNECTORS_ATTACHED_SERVICE_CANONICAL = 'connectors-feature-access'
/** Legacy mistaken id persisted by older frontend builds — treat as canonical for reads. */
const CONNECTORS_ATTACHED_SERVICE_DEPRECATED = 'connections-feature-access'

/** Service strings that refer to the same Connections scoped policy row. */
function attachedPolicyAliasesForService(service: string): readonly string[] {
  if (
    service === CONNECTORS_ATTACHED_SERVICE_CANONICAL ||
    service === CONNECTORS_ATTACHED_SERVICE_DEPRECATED
  ) {
    return [CONNECTORS_ATTACHED_SERVICE_CANONICAL, CONNECTORS_ATTACHED_SERVICE_DEPRECATED]
  }
  return [service]
}

export function findAttachedPolicyEntry(
  attachedPolicies: AttachedPolicyEntry[] | null | undefined,
  service: string,
): AttachedPolicyEntry | undefined {
  const list = attachedPolicies ?? []
  for (const s of attachedPolicyAliasesForService(service)) {
    const hit = list.find((p) => p.service === s)
    if (hit) return hit
  }
  return undefined
}

/** Read the `actions` map for `service` from a role's `attached_policies`. */
export function getPolicyActions(
  attachedPolicies: AttachedPolicyEntry[] | null | undefined,
  service: string,
): Record<string, boolean> {
  return findAttachedPolicyEntry(attachedPolicies, service)?.actions ?? {}
}

/** Build a policy entry; returns null when there is no signal to persist —
 *  no enabled actions (core or child) AND the UI eye is in its default state (visible).
 *  Explicitly hidden UI (`viewActions === false`, either on the parent or any child)
 *  IS a signal and must round-trip, otherwise disabling the eye gets dropped on save. */
export function buildAttachedPolicy(
  service: string,
  actions: Record<string, boolean>,
  viewActions: boolean,
  children?: AttachedPolicyEntry[],
): AttachedPolicyEntry | null {
  const hasCore = Object.values(actions).some(Boolean)
  const hasChild = children?.some(
    (c) =>
      Object.values(c.actions ?? {}).some(Boolean) ||
      c.view_actions === false ||
      c.view_actions === true,
  )
  const uiHidden = viewActions === false
  if (!hasCore && !hasChild && !uiHidden) return null
  const entry: AttachedPolicyEntry = { service, actions, view_actions: viewActions }
  if (children && children.length > 0) {
    entry.children = children
  }
  return entry
}

/** Return all attached policies except the ones for `services`. */
export function filterOtherPolicies(
  attachedPolicies: AttachedPolicyEntry[] | null | undefined,
  services: string | string[],
): AttachedPolicyEntry[] {
  const skip = new Set(Array.isArray(services) ? services : [services])
  for (const s of [...skip]) {
    for (const a of attachedPolicyAliasesForService(s)) {
      skip.add(a)
    }
  }
  return (attachedPolicies ?? []).filter((p) => !skip.has(p.service))
}

/** Read legacy `role.permissions[legacyKey]` as an actions map. */
export function legacyPermissionsToActions(
  permissions: Record<string, unknown> | null | undefined,
  legacyKey: string,
  actionNames: readonly string[],
): Record<string, boolean> {
  const perms = permissions?.[legacyKey] ?? {}
  return Object.fromEntries(actionNames.map((a) => [a, Boolean(perms[a])]))
}

// ---------------------------------------------------------------------------
// Scoped-feature registry
// ---------------------------------------------------------------------------
// One entry per feature that uses the attached_policies RBAC model.
// Adding a new feature: add an entry here, then it shows up in useCanAccess
// and RolePage automatically. Use `extraActions` for optional toggles beyond V/C/E/D.

/** Metadata for a sub-feature (tab/section) within a scoped feature. */
export interface SubFeatureMeta {
  displayName: string
  actions: readonly FeatureAction[]
  /** Visual grouping hint for the Role Admin UI. Sub-features sharing the same
   *  `parentTab` render under a collapsible group header. `null` = top-level. */
  parentTab?: string | null
  /** UI tab/route key when it differs from the sub-feature key.
   *  Eliminates the need for a separate TAB_TO_SUB_FEATURE mapping. */
  tabKey?: string
}

export interface ScopedFeature {
  /** Frontend feature id used in canAccess(feature, ...) and `permissions[key]`. */
  featureKey: string
  /** Backend service id stored in `attached_policies[].service`. */
  service: string
  /** Key in `role.permissions` for the legacy fallback. */
  legacyKey: string
  /** Core RBAC actions every feature exposes in the V/C/E/D grid. */
  coreActions: readonly FeatureAction[]
  /** Additional actions surfaced as separate toggles (beyond V/C/E/D). */
  extraActions: readonly string[]
  /** Sub-tab/sub-section definitions for granular per-tab role control.
   *  When present, the Role Admin page renders an expandable sub-feature grid. */
  subFeatures?: Record<string, SubFeatureMeta>
}

// Order matches sidebar navigation: Top → Blueprints → Develop → Observability → Ops
// Features NOT listed here have no attached_policies RBAC and use the legacy role.permissions dict.
//
// The data shape mirrors backend role JSON (`service`, `actions`, `view_actions?`, `children?`)
// so reading a role policy and the registry definition feel the same. UI extras (`displayName`,
// `tabKey`) and frontend-app fields (`featureKey`, `legacyKey`, `extraActions`) live alongside.
// Internal nodes (those with non-empty `children`) act as parent-tab groupings — they're not
// sub-features themselves; only leaves are.
//
// `flattenRoot` derives the legacy `subFeatures: Record<...>` view at module load, so all
// existing consumers (RolePage, useCanAccess, etc.) keep working without changes.
const SCOPED_FEATURES_DATA = [
  // ── Enterprise Ontology ─────────────────────────────────────────────────────
  {
    featureKey: 'ontology',
    service: 'ontology-feature-access',
    legacyKey: 'ontology',
    displayName: 'Ontology',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'canvas',        displayName: 'Canvas',        actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'onto_eval',     displayName: 'Onto Eval',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'data_models',   displayName: 'Data Models',   actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'kpi_library',   displayName: 'KPI Library',   actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'semantic_hub',  displayName: 'Semantic Hub',  actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'migrations',    displayName: 'Migrations',    actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'build_with_ai',  displayName: 'Build with AI', actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  // ── Top nav ─────────────────────────────────────────────────────────────────
  // Home is a read-only dashboard — every endpoint behind it is a GET, so it
  // exposes `view` only and the role editor renders a single V toggle.
  {
    featureKey: 'home',
    service: 'home-feature-access',
    legacyKey: 'home',
    displayName: 'Home',
    actions: ['view'],
    extraActions: [],
    children: [
      { service: 'overview',  displayName: 'Overview',  actions: ['view'], children: [] },
      { service: 'agents',    displayName: 'Agents',    actions: ['view'], children: [] },
      { service: 'workflows', displayName: 'Workflows', actions: ['view'], children: [] },
      { service: 'evals',     displayName: 'Evals',     actions: ['view'], children: [] },
      { service: 'sessions',  displayName: 'Sessions',  actions: ['view'], children: [] },
      { service: 'cost',      displayName: 'Cost',      actions: ['view'], children: [] },
    ],
  },
  {
    featureKey: 'solutions',
    service: 'solutions-feature-access',
    legacyKey: 'solutions',
    displayName: 'Solutions',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'agent-store',
    service: 'agent-store-feature-access',
    legacyKey: 'agent-store',
    displayName: 'Agent Store',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'workflow-store',
    service: 'workflow-store-feature-access',
    legacyKey: 'workflow-store',
    displayName: 'Workflow Store',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  // ── Blueprints ───────────────────────────────────────────────────────────────
  {
    featureKey: 'ai-blueprints',
    service: 'ai-blueprints-feature-access',
    legacyKey: 'ai-blueprints',
    displayName: 'Blueprints',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'ideate',
    service: 'ideate-feature-access',
    legacyKey: 'ideate',
    displayName: 'Ideate',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  // ── Agent Development ────────────────────────────────────────────────────────
  {
    featureKey: 'projects',
    service: 'projects-feature-access',
    legacyKey: 'projects',
    displayName: 'Projects',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'setup_wizard', displayName: 'Setup Wizard', actions: ['view', 'create', 'edit', 'delete'], children: [] },
      {
        service: 'configure',
        displayName: 'Configure',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'members',       displayName: 'Members',       actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'orchestrators', displayName: 'Orchestrators', actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'connections', displayName: 'Connections', actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'identities', displayName: 'Agent identities', actions: [], children: [] },
          { service: 'prompts',       displayName: 'Prompts',       actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'models',        displayName: 'Models',        actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'tools',         displayName: 'Tools',         actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'skills',        displayName: 'Skills',        actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'knowledge',     displayName: 'Knowledge',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'graph',         displayName: 'Graph',         actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'vector',        displayName: 'Vector',        actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'storage',       displayName: 'Storage',       actions: ['view', 'create', 'edit', 'delete'], children: [] },
        ],
      },
      {
        service: 'details',
        displayName: 'Details',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'overview',     displayName: 'Overview',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'agents',       displayName: 'Agents',       actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'workflows',    displayName: 'Workflows',    actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'registry',     displayName: 'Registry',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'evals',        displayName: 'Evals',        actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'deployments',  displayName: 'Deployments',  actions: ['view', 'create', 'edit', 'delete'], children: [] },
        ],
      },
    ],
  },
  {
    featureKey: 'data',
    service: 'data-feature-access',
    legacyKey: 'data',
    displayName: 'Data',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'knowledge', displayName: 'Knowledge', actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'graph',     displayName: 'Graph',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'vector_db', displayName: 'Vector DB', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'vector', children: [] },
      { service: 'storage',   displayName: 'Storage',   actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  {
    featureKey: 'prompts',
    service: 'prompts-feature-access',
    legacyKey: 'prompts',
    displayName: 'Prompts',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'prompt_optimization', displayName: 'Optimize Prompts', actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  {
    featureKey: 'skills',
    service: 'skills-feature-access',
    legacyKey: 'skills',
    displayName: 'Skills',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'agent-studio',
    service: 'agent-studio-feature-access',
    legacyKey: 'agent-studio',
    displayName: 'Agent Studio',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'auto_tune',     displayName: 'Auto Tune',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'build_with_ai', displayName: 'Build with AI', actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  {
    featureKey: 'agent-workflows',
    service: 'agent-workflows-feature-access',
    legacyKey: 'agent-workflows',
    displayName: 'Agent Workflows',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'workflows',          displayName: 'Workflows',          actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'solution_templates', displayName: 'Solution Templates', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'templates', children: [] },
      { service: 'build_with_ai',      displayName: 'Build with AI',      actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'metrics',            displayName: 'Metrics',            actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  // ── Observability ────────────────────────────────────────────────────────────
  {
    featureKey: 'agent-registry',
    service: 'registry-feature-access',
    legacyKey: 'agent-registry',
    displayName: 'Agent Registry',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'agents',    displayName: 'Agents',    actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'workflows', displayName: 'Workflows', actions: ['view', 'create', 'edit', 'delete'], children: [] },
      {
        service: 'guardrails',
        displayName: 'Guardrails',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'guardrails_repository', displayName: 'Guardrails Repository', actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'agent_guardrails',      displayName: 'Agent Guardrails',      actions: ['view', 'create', 'edit', 'delete'], children: [] },
        ],
      },
      {
        service: 'security',
        displayName: 'Security',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'coverage', displayName: 'Coverage', actions: ['view', 'create', 'edit', 'delete'], children: [] },
        ],
      },
    ],
  },
  {
    featureKey: 'evals-simulations',
    service: 'evals-simulations-feature-access',
    legacyKey: 'evals-simulations',
    displayName: 'Evals & Simulations',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      // Leaderboards / Simulations / Environments are hidden (flags off) and omitted here,
      // so they never appear on the Roles page. Backend policy still owns their routes.
      { service: 'benchmarks',   displayName: 'Benchmarks',   actions: ['view', 'create', 'edit', 'delete'], children: [] },
      {
        service: 'evaluations',
        displayName: 'Evaluations',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          // Eval Runs covers the whole Agent Evals area: agent/workflow list, sessions,
          // Analytics, Metrics & Plots, Insights & Diagnostics.
          { service: 'eval_runs',             displayName: 'Eval Runs',      actions: ['view', 'create', 'edit', 'delete'], tabKey: 'evals',     children: [] },
          { service: 'agent_evals_templates', displayName: 'Eval Templates', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'templates', children: [] },
        ],
      },
    ],
  },
  // ── Benchmark Studio ─────────────────────────────────────────────────────────
  // Backend has policy/benchmark_studio_feature_access.py + BENCHMARK_STUDIO_RBAC, and
  // the studio is entered from the Benchmarks tab ("Enrich" on a dataset row), so it
  // has no nav item by design. It is NOT listed here, and cannot be rendered in the
  // role editor, because there is no `benchmark_studio_view` flag key anywhere
  // (not in FEATURE_FLAG_META, featureFlagsTree.ts, or feature_flags_master.yaml).
  // RolePage.isFeatureEnabled resolves that key and returns false for an unknown flag,
  // so the row would be silently dropped even if declared. Surfacing it needs a real
  // view flag — a decision, not a wiring fix. See the note in flag_gate.py.
  {
    featureKey: 'monitoring',
    service: 'monitoring-feature-access',
    legacyKey: 'monitoring',
    displayName: 'Monitoring',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      {
        service: 'deployments',
        displayName: 'Deployments',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'dep_agents',    displayName: 'Agents',    actions: ['view', 'create', 'edit', 'delete'], tabKey: 'agents',    children: [] },
          { service: 'dep_workflows', displayName: 'Workflows', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'workflows', children: [] },
        ],
      },
      { service: 'hitl_inbox',    displayName: 'HITL Inbox',    actions: ['view', 'create', 'edit', 'delete'], tabKey: 'hitl-inbox',    children: [] },
      { service: 'rag_analytics', displayName: 'RAG Analytics', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'rag-analytics', children: [] },
      {
        service: 'token-usage',
        displayName: 'Token Usage',
        actions: ['view', 'create', 'edit', 'delete'],
        children: [
          { service: 'consumption', displayName: 'Consumption', actions: ['view', 'create', 'edit', 'delete'], children: [] },
          { service: 'llm_usage',   displayName: 'LLM Usage',   actions: ['view', 'create', 'edit', 'delete'], tabKey: 'llm-usage', children: [] },
        ],
      },
      { service: 'cache',      displayName: 'Cache',      actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'guardrails', displayName: 'Guardrails', actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'security',   displayName: 'Security',   actions: ['view', 'create', 'edit', 'delete'], tabKey: 'security', children: [] },
      { service: 'voice',      displayName: 'Voice',      actions: ['view', 'create', 'edit', 'delete'], tabKey: 'voice', children: [] },
      { service: 'policies',   displayName: 'Policies',   actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'alerts',     displayName: 'Alerts',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'jobs',        displayName: 'Monitoring Jobs', actions: ['view', 'create', 'edit', 'delete'], tabKey: 'monitor-jobs', children: [] },
      { service: 'performance', displayName: 'Quality & Evals', actions: ['view'], tabKey: 'quality-evals', children: [] },
    ],
  },
  // ── Agent Ops ────────────────────────────────────────────────────────────────
  {
    featureKey: 'models',
    service: 'models-feature-access',
    legacyKey: 'models',
    displayName: 'Models',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'model_arena',     displayName: 'Model Arena',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'pricing_catalog', displayName: 'Pricing Catalog', actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
  {
    featureKey: 'tools',
    service: 'tools-feature-access',
    legacyKey: 'tools',
    displayName: 'Tools',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  // ── Resources ────────────────────────────────────────────────────────────────
  {
    featureKey: 'docs',
    service: 'docs-feature-access',
    legacyKey: 'docs',
    displayName: 'Docs',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'agent-identities',
    service: 'agent-identities-feature-access',
    legacyKey: 'agent-identities',
    displayName: 'Agent identities',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [],
  },
  {
    featureKey: 'connections',
    service: 'connectors-feature-access',
    legacyKey: 'connections',
    displayName: 'Connections',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'databases',        displayName: 'Databases',        actions: [], children: [] },
      { service: 'storage_accounts', displayName: 'Storage Accounts', actions: [], tabKey: 'storage', children: [] },
      { service: 'orchestrators',    displayName: 'Orchestrators',    actions: [], children: [] },
      { service: 'telephony',        displayName: 'Telephony',        actions: [], children: [] },
      { service: 'service_connections', displayName: 'Service Connections', actions: [], children: [] },
    ],
  },
  // ── Voice Agents ─────────────────────────────────────────────────────────────
  // Registered in the backend (policy/voice_agents_feature_access.py, VOICE_AGENTS_RBAC,
  // roles_config) and deployed in feature_flags_master.yaml, but absent here — so no
  // role editor row existed and the grants were ungovernable from the Admin UI.
  // Renders as a nav-exempt row (see NAV_EXEMPT_FEATURES in RolePage.tsx).
  {
    featureKey: 'voice-agents',
    service: 'voice-agents-feature-access',
    legacyKey: 'voice-agents',
    displayName: 'Voice Agents',
    actions: ['view', 'create', 'edit', 'delete'],
    extraActions: [],
    children: [
      { service: 'manage_numbers',     displayName: 'Manage Numbers',     actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'manage_calls',       displayName: 'Manage Calls',       actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'manage_recordings',  displayName: 'Manage Recordings',  actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'analytics',          displayName: 'Analytics',          actions: ['view', 'create', 'edit', 'delete'], children: [] },
      { service: 'canvas',             displayName: 'Canvas',             actions: ['view', 'create', 'edit', 'delete'], children: [] },
    ],
  },
] as const

// ---------------------------------------------------------------------------
// Nested → flat conversion
// ---------------------------------------------------------------------------
// Internal node shape used by the flatten function. The public `ScopedFeature`
// (with `coreActions` and `subFeatures`) is what every consumer sees at runtime.

interface ScopedNodeRaw {
  readonly service: string
  readonly displayName: string
  readonly actions: readonly FeatureAction[]
  readonly view_actions?: boolean
  readonly tabKey?: string
  readonly children?: readonly ScopedNodeRaw[]
}

interface ScopedRootRaw extends ScopedNodeRaw {
  readonly featureKey: string
  readonly legacyKey: string
  readonly extraActions?: readonly string[]
}

/** Walk a `children` tree; every node (leaves AND internal) becomes a `subFeatures` entry.
 *  Each entry's `parentTab` points to its nearest internal-node ancestor so the UI can
 *  group children under their parent row. Internal nodes get their own row WITH actions
 *  (matching their `{path}_view` flag in the feature-flag catalog). */
function collectSubFeatures(
  nodes: readonly ScopedNodeRaw[] | undefined,
  parentTab: string | null,
  acc: Record<string, SubFeatureMeta>,
): void {
  if (!nodes) return
  for (const node of nodes) {
    const meta: SubFeatureMeta = {
      displayName: node.displayName,
      actions: node.actions,
    }
    if (parentTab !== null) meta.parentTab = parentTab
    if (node.tabKey) meta.tabKey = node.tabKey
    acc[node.service] = meta
    if (node.children && node.children.length > 0) {
      collectSubFeatures(node.children, node.service, acc)
    }
  }
}

/** Convert one nested root into the legacy flat `ScopedFeature` shape. */
function flattenRoot(root: ScopedRootRaw): ScopedFeature {
  const subFeatures: Record<string, SubFeatureMeta> = {}
  collectSubFeatures(root.children, null, subFeatures)
  const feat: ScopedFeature = {
    featureKey: root.featureKey,
    service: root.service,
    legacyKey: root.legacyKey,
    coreActions: root.actions,
    extraActions: root.extraActions ?? [],
  }
  if (Object.keys(subFeatures).length > 0) {
    feat.subFeatures = subFeatures
  }
  return feat
}

/** Public flat view of the registry — derived from SCOPED_FEATURES_DATA at module load.
 *  All existing iteration code (RolePage, useCanAccess, buildFlatPermMap, …) consumes this. */
export const SCOPED_FEATURES: readonly ScopedFeature[] =
  (SCOPED_FEATURES_DATA as readonly ScopedRootRaw[]).map(flattenRoot)

// ---------------------------------------------------------------------------
// Type-safe sub-feature key helpers (derived from SCOPED_FEATURES_DATA literal)
// ---------------------------------------------------------------------------
// These types let `useTabVisibility(featureKey)` return a `canViewTab` that
// only accepts valid sub-feature / tab keys for that specific feature.
// Adding a new feature or sub-feature in SCOPED_FEATURES_DATA automatically
// updates the unions — no per-call constants to maintain, no typo-prone literals.

/** All valid feature keys defined in SCOPED_FEATURES. */
export type ScopedFeatureKey = (typeof SCOPED_FEATURES_DATA)[number]['featureKey']

/** The frozen literal entry whose `featureKey` is F. */
type FeatureByKey<F extends ScopedFeatureKey> = Extract<
  (typeof SCOPED_FEATURES_DATA)[number],
  { readonly featureKey: F }
>

/** True if `children` is a non-empty tuple. `children: []` (empty leaf marker) is false. */
type HasNonEmptyChildren<N> =
  N extends { readonly children: readonly [unknown, ...unknown[]] } ? true : false

/** Recursively walk a node-tree type, emitting the `service` of every LEAF.
 *  Leaves include nodes with a missing or empty `children` field. Internal nodes
 *  (non-empty `children`) are descended-into but their own service is skipped. */
type LeafServicesOf<N> =
  HasNonEmptyChildren<N> extends true
    ? N extends { readonly children: infer C extends readonly unknown[] }
      ? LeafServicesOf<C[number]>
      : never
    : N extends { readonly service: infer S extends string }
      ? S
      : never

/** Recursively walk a node-tree type, emitting every `service` and every `tabKey`
 *  encountered (leaves + internals). Used for `canViewTab` parameter typing.
 *  Empty `children: []` arrays terminate recursion safely (C[number] is never). */
type AllNodeKeysOf<N> =
  | (N extends { readonly service: infer S extends string } ? S : never)
  | (N extends { readonly tabKey: infer T extends string } ? T : never)
  | (N extends { readonly children: infer C extends readonly unknown[] }
      ? AllNodeKeysOf<C[number]>
      : never)

/** Sub-feature (leaf) keys for feature F — excludes parent-tab containers and tabKey aliases. */
export type SubFeatureKeyOf<F extends ScopedFeatureKey> =
  FeatureByKey<F> extends { readonly children: infer C extends readonly unknown[] }
    ? LeafServicesOf<C[number]>
    : never

/** All keys accepted by `canViewTab` for feature F:
 *  - sub-feature (leaf) keys (e.g. 'auto_tune', 'build_with_ai')
 *  - their `tabKey` aliases (e.g. 'evals' for 'agent_evals')
 *  - parent-tab container keys (e.g. 'evaluations', 'guardrails')
 */
export type TabKeyOf<F extends ScopedFeatureKey> =
  FeatureByKey<F> extends { readonly children: infer C extends readonly unknown[] }
    ? AllNodeKeysOf<C[number]>
    : never

/** Read the `children` array for `service` from a role's `attached_policies`. */
export function getPolicyChildren(
  attachedPolicies: AttachedPolicyEntry[] | null | undefined,
  service: string,
): AttachedPolicyEntry[] {
  return findAttachedPolicyEntry(attachedPolicies, service)?.children ?? []
}

/**
 * Sub-features that replaced older ones without a data migration, per feature key.
 * Until a role is re-saved with the new node, its eye/grants come from the OR of the
 * stored legacy nodes. Mirrors LEGACY_CHILD_FALLBACKS in backend policy_evaluator.py.
 */
export const LEGACY_SUBFEATURE_FALLBACKS: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  // Evals: Eval Runs replaced Agent Evals + Workflow Evals (2026-10).
  'evals-simulations': { eval_runs: ['agent_evals', 'workflow_evals'] },
}

/**
 * Build a flat permission map from attached_policies for fast runtime key lookups.
 *
 * Keys:
 *   `fk`              → parent view (e.g. `evals_simulations`)
 *   `fk_action`       → parent action  (e.g. `evals_simulations_view`)
 *   `fk_sk`           → child/tab view (e.g. `evals_simulations_benchmarks`)
 *   `fk_sk_action`    → child action   (e.g. `evals_simulations_benchmarks_view`)
 *   `fk_sk_gk`        → grandchild view full path (e.g. `evals_simulations_evaluations_eval_runs`)
 *   `fk_sk_gk_action` → grandchild action full path
 *   `fk_gk`           → grandchild shortcut (skips parent-tab prefix, keeps lookup simple)
 *   `fk_gk_action`    → grandchild action shortcut
 */
export function buildFlatPermMap(
  attachedPolicies: AttachedPolicyEntry[] | null | undefined,
): Record<string, boolean> {
  const flat: Record<string, boolean> = {}

  function emitGrandchildShortcuts(prefix: string, grandchild: AttachedPolicyEntry) {
    const gk = grandchild.service.replace(/-/g, '_')
    const shortcut = `${prefix}_${gk}`
    const gview = grandchild.view_actions ?? Boolean(grandchild.actions?.view)
    if (!(shortcut in flat)) flat[shortcut] = gview
    if (!(`${shortcut}_view` in flat)) flat[`${shortcut}_view`] = gview
    for (const [a, v] of Object.entries(grandchild.actions ?? {})) {
      if (a === 'view') continue
      const sk2 = `${shortcut}_${a}`
      if (!(sk2 in flat)) flat[sk2] = Boolean(v)
    }
  }

  function emitEntry(prefix: string, entry: AttachedPolicyEntry) {
    const sk = entry.service.replace(/-/g, '_')
    const key = `${prefix}_${sk}`
    // view_actions is the authoritative source for UI visibility (eye toggle).
    // actions.view drives API statement expansion — kept separate.
    const view = entry.view_actions ?? Boolean(entry.actions?.view)
    flat[key] = view
    flat[`${key}_view`] = view  // _view key = view_actions, not actions.view
    for (const [a, v] of Object.entries(entry.actions ?? {})) {
      if (a !== 'view') flat[`${key}_${a}`] = Boolean(v)
    }
    for (const grandchild of entry.children ?? []) {
      emitEntry(key, grandchild)
      // Shortcut: feature_grandchild (without the parent-tab segment) so
      // checkSubFeaturePermission works without knowing the nesting depth.
      emitGrandchildShortcuts(prefix, grandchild)
    }
  }

  for (const entry of attachedPolicies ?? []) {
    const feat =
      SCOPED_FEATURES.find((f) => f.service === entry.service) ??
      (entry.service === CONNECTORS_ATTACHED_SERVICE_DEPRECATED
        ? SCOPED_FEATURES.find((f) => f.service === CONNECTORS_ATTACHED_SERVICE_CANONICAL)
        : undefined)
    if (!feat) continue
    const fk = feat.featureKey.replace(/-/g, '_')
    const view = entry.view_actions ?? Boolean(entry.actions?.view)
    flat[fk] = view
    flat[`${fk}_view`] = view  // view_actions drives UI, not actions.view
    for (const [a, v] of Object.entries(entry.actions ?? {})) {
      if (a !== 'view') flat[`${fk}_${a}`] = Boolean(v)
    }
    for (const child of entry.children ?? []) {
      emitEntry(fk, child)
    }
  }
  return flat
}

const CRUD_ACTIONS = ['view', 'create', 'edit', 'delete'] as const

/** Actions this row is allowed to toggle. View-only rows omit create/edit/delete. */
export function declaredCrudActions(meta: SubFeatureMeta): FeatureAction[] {
  return CRUD_ACTIONS.filter((action) => meta.actions.includes(action))
}

/** True when a sub-feature exposes only the UI eye toggle (no V/C/E/D grid). */
export function isUiOnlySubFeature(meta: SubFeatureMeta): boolean {
  return meta.actions.length === 0
}

/**
 * Whether the role grid shows action buttons for this row.
 * Full V/C/E/D containers (Evaluations, Guardrails) stay eye-only groupings.
 * A container that declares a smaller set (Agent Process Metrics → view) still shows those buttons.
 */
export function showsSubFeatureActions(meta: SubFeatureMeta, isContainer: boolean): boolean {
  if (isUiOnlySubFeature(meta)) return false
  if (isContainer && declaredCrudActions(meta).length === CRUD_ACTIONS.length) return false
  return true
}

/** Persist only actions the row declares. Undeclared actions are an explicit deny.
 *  `alwaysGranted` marks a feature whose V/C/E/D is not role-managed (see
 *  ALWAYS_GRANTED_FEATURES): every declared action is written on regardless of any
 *  stored per-row value, while undeclared actions stay denied. */
export function savedSubFeatureActions(
  meta: SubFeatureMeta,
  subPerms: Record<string, boolean> | undefined,
  parentActions: Record<string, boolean>,
  alwaysGranted = false,
): Record<string, boolean> {
  const declared = new Set<string>(declaredCrudActions(meta))
  return Object.fromEntries(
    CRUD_ACTIONS.map((action) => {
      if (!declared.has(action)) return [action, false]
      if (alwaysGranted) return [action, true]
      const explicit = subPerms != null && Object.hasOwn(subPerms, action)
      const granted = explicit ? Boolean(subPerms[action]) : Boolean(parentActions[action])
      return [action, granted]
    }),
  )
}

/** Resolve the effective action value for a sub-feature.
 *  If the sub-feature has an explicit value, use it; otherwise inherit from parent `actions`. */
export function resolveSubFeatureAction(
  parentActions: Record<string, boolean>,
  subFeatureActions: Record<string, boolean> | undefined,
  action: string,
): boolean {
  if (subFeatureActions && action in subFeatureActions) {
    return Boolean(subFeatureActions[action])
  }
  return Boolean(parentActions[action])
}

/** Sub-feature keys that wrap nested children (e.g. `configure`, `guardrails`).
 *  These are UI grouping containers — VCED is inherited from the parent feature on save. */
export function getParentTabContainerKeys(
  subFeatures: Record<string, SubFeatureMeta>,
): Set<string> {
  const containers = new Set<string>()
  for (const meta of Object.values(subFeatures)) {
    if (meta.parentTab) containers.add(meta.parentTab)
  }
  return containers
}

/** Group sub-features by their parentTab for UI rendering.
 *  Returns an ordered array of `{ parentTab, subKeys }` entries.
 *
 *  `subFeatures` is in DFS pre-order (every parent is immediately followed by
 *  its descendants — see `collectSubFeatures`). We start a new group on every
 *  `parentTab` transition, producing contiguous runs that keep each child group
 *  directly under its parent row. A given `parentTab` (including the top-level
 *  `null`) can therefore appear in multiple groups — e.g. the top-level run is
 *  split so `Evaluations`' children render right after the `Evaluations` row,
 *  not after the last top-level sibling.
 */
export function groupSubFeaturesByParentTab(
  subFeatures: Record<string, SubFeatureMeta>,
): { parentTab: string | null; subKeys: string[] }[] {
  const result: { parentTab: string | null; subKeys: string[] }[] = []
  for (const [key, meta] of Object.entries(subFeatures)) {
    const tab = meta.parentTab ?? null
    const last = result[result.length - 1]
    if (last && last.parentTab === tab) {
      last.subKeys.push(key)
    } else {
      result.push({ parentTab: tab, subKeys: [key] })
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Tab key ↔ sub-feature key mapping  (derived from SCOPED_FEATURES)
// ---------------------------------------------------------------------------
// Derived automatically from SubFeatureMeta.tabKey and parentTab values.
// No separate static mapping to maintain — set `tabKey` on the sub-feature
// when the UI tab key differs from the sub-feature key.

function buildTabToSubFeatureMap(): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {}
  for (const feat of SCOPED_FEATURES) {
    if (!feat.subFeatures) continue
    map[feat.featureKey] = {}
    const parentTabs = new Set<string>()
    for (const [subKey, meta] of Object.entries(feat.subFeatures)) {
      // Direct mapping: tabKey (when set) or sub-feature key → sub-feature key
      map[feat.featureKey][meta.tabKey ?? subKey] = subKey
      if (meta.parentTab) parentTabs.add(meta.parentTab)
    }
    // Parent-tab group keys map to themselves so canViewUI can resolve the container
    for (const pt of parentTabs) {
      if (!(pt in map[feat.featureKey])) {
        map[feat.featureKey][pt] = pt
      }
    }
  }
  return map
}

const DERIVED_TAB_MAP = buildTabToSubFeatureMap()

/** Resolve a page tab key to its RBAC sub-feature key.
 *  Returns `undefined` if the feature has no sub-feature mapping for the tab. */
export function tabToSubFeature(featureKey: string, tabKey: string): string | undefined {
  return DERIVED_TAB_MAP[featureKey]?.[tabKey]
}

/** Get the sub-feature key for a tab, with a fallback to the tab key itself. */
export function requireSubFeatureForTab(featureKey: string, tabKey: string): string {
  return DERIVED_TAB_MAP[featureKey]?.[tabKey] ?? tabKey
}

/** All actions (core + extra) for a feature. */
export function allActionsFor(feature: ScopedFeature): readonly string[] {
  return [...feature.coreActions, ...feature.extraActions]
}

/** The full V/C/E/D set — what a feature exposes unless its registry entry narrows it. */
export const ALL_CORE_ACTIONS: readonly FeatureAction[] = ['view', 'create', 'edit', 'delete']

/** Core actions a feature exposes in the role editor grid, by feature/legacy key.
 *  Read-only features (e.g. `home`) declare a narrower set in SCOPED_FEATURES_DATA;
 *  features outside the scoped registry keep the full V/C/E/D set. */
export function coreActionsFor(featureId: string): readonly FeatureAction[] {
  return findScopedFeature(featureId)?.coreActions ?? ALL_CORE_ACTIONS
}

/**
 * Services whose access checks are wired up in backend `rbac_registry.py`
 * (i.e. backend reads `attached_policies` for these). For any scoped feature
 * NOT in this set, the backend has no policy evaluator yet, so the save path
 * must also mirror V/C/E/D into legacy `role.permissions[legacyKey]` so
 * legacy backend checks still see the grant.
 *
 * Keep in sync with `SERVICE_ACTION_MAPS` in
 * `backend/app/core/role_permissions/rbac_registry.py`.
 */
export const BACKEND_RBAC_SERVICES: ReadonlySet<string> = new Set([
  'skills-feature-access',
  'evals-simulations-feature-access',
  'agent-studio-feature-access',
  'agent-workflows-feature-access',
  'prompts-feature-access',
  'projects-feature-access',
  'tools-feature-access',
  'models-feature-access',
  'solutions-feature-access',
  'registry-feature-access',
  'connectors-feature-access',
  'agent-identities-feature-access',
])

/** True when backend `rbac_registry.py` evaluates attached_policies for this feature. */
export function featureHasBackendRBAC(feature: ScopedFeature): boolean {
  return BACKEND_RBAC_SERVICES.has(feature.service)
}

export function findScopedFeature(featureKey: string): ScopedFeature | undefined {
  const n = featureKey.toLowerCase().replace(/-/g, '_')
  return SCOPED_FEATURES.find((f) => {
    const fk = f.featureKey.toLowerCase().replace(/-/g, '_')
    const lk = f.legacyKey.toLowerCase().replace(/-/g, '_')
    return fk === n || lk === n
  })
}

